import {
    faExclamationCircle,
    faExclamationTriangle,
    faMagnifyingGlass,
    faNote,
    IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { css, RuleSet } from 'styled-components';
import { tokens } from 'styles/tokens';
import { faIconBefore } from '../icon';

// --- Example Input: ---

// > [!Evidence: Found Evidence]
// > 1. Measured vibration increase at 1x, 3x, 9x
// > 2. Caroline reported a louder continuous noise from the device at 09:20
// > 3. An attached video corroborates apparent looseness

type BlockquoteTypes = 'Evidence' | 'Note' | 'Alarm' | 'Warning';

type BlockquoteThemeValues = {
    className: string;
    accentColor: string;
    icon: IconDefinition;
    background?: string;
    textColor?: string;
    collapsibleOverride?: boolean;
};

const themeValues: Record<BlockquoteTypes, BlockquoteThemeValues> = {
    Evidence: {
        className: 'evidence',
        accentColor: tokens.color.blue500,
        icon: faMagnifyingGlass,
        collapsibleOverride: true,
    },
    Note: {
        className: 'note',
        accentColor: tokens.color.grey500,
        icon: faNote,
    },
    Alarm: {
        className: 'alarm',
        accentColor: tokens.color.red500,
        icon: faExclamationCircle,
        background: tokens.alert.errorBackground,
        // TODO: consider text intensity
        // textColor: tokens.alert.errorColor,
    },
    Warning: {
        className: 'warning',
        accentColor: tokens.color.orange500,
        icon: faExclamationTriangle,
        background: tokens.alert.warningBackground,
        // textColor: tokens.alert.warningColor,
    },
};

// specialBlockquote: transforms a single blockquote if it has a [!Type] header
const QUOTE_START = /^>\s*/.source;
const TYPE = /(\w+)/.source;
const TITLE = /(?::\s*([^\]]+))?/.source;
const CALLOUT = new RegExp(`${QUOTE_START}\\[!${TYPE}${TITLE}\\]`);

function specialBlockquote(text: string): string {
    const header = text.match(CALLOUT);
    if (!header) {
        return text;
    }

    const type = header[1];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const title = header[2]?.trim() ?? type;
    const body = text
        .split(/\r?\n/)
        .slice(1)
        .map((line) => line.replace(/^>\s?/, ''))
        .join('\n');

    const values = themeValues[type as BlockquoteTypes];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!values) {
        return `<div>\n\n<strong>${title}</strong>\n\n${body}\n\n</div>`;
    }

    const { className, collapsibleOverride } = values;

    if (collapsibleOverride) {
        return `<details class="${className}"><summary><strong>${title}</strong></summary>\n\n${body}\n\n</details>`;
    }

    if (className) {
        return `<div class="${className}">\n\n<strong>${title}</strong>\n\n${body}\n\n</div>`;
    }

    return `<div>\n\n<strong>${title}</strong>\n\n${body}\n\n</div>`;
}

// top-level pass -- regex finds blockquotes, callback transforms special ones
export function transformBlockquotes(text: string): string {
    return text.replace(/^>.*(?:\r?\n>.*)*$/gm, specialBlockquote);
}

function blockquoteStyle(type: BlockquoteTypes): RuleSet<object> {
    const { className, accentColor, icon, background, textColor } = themeValues[type];

    return css`
div.${className} {
    padding: 1rem;
}

.${className} {
    background-color: ${background ?? tokens.comment.blockquoteBackground};
    color: ${textColor ?? tokens.text.primary};

    border-radius: 8px;
    border: 1px solid ${tokens.border.default};
    border-left: 4px solid ${accentColor};

    margin-bottom: 0.8rem;
    & > :first-child {
        ${faIconBefore(icon, accentColor)}
    }
}
`;
}

const detailsStyles = css`
    details {
        /* close: width shrinks after height collapses */
        transition: max-width 0.2s ease-in 0.2s;
        max-width: 150px;
        height: auto;

        padding: 0;
        summary {
            padding: .6rem;
        }

        summary {
            display: flex;
            align-items: center;
            list-style: none;
            cursor: pointer;

            &:hover, &:focus-visible {
                background-color: ${tokens.action.hover};
            }
            &:active {
                background-color: ${tokens.action.active};
            }
        }

        summary::-webkit-details-marker {
            display: none;
        }

        /* custom arrow on right side */
        summary::after {
            content: "▶";
            font-size: 1.6rem;
            transition: transform 0.2s ease;
            margin-left: auto;
        }
    }

    details::details-content {
        max-height: 0;
        overflow: clip;
        padding-inline: 1rem;
        /* close: height collapses first */
        transition:
            max-height 0.2s ease-out,
            padding-bottom 0.2s ease-out,
            content-visibility 0.2s ease-out allow-discrete;
    }

    details[open] {
        /* open: width expands first */
        transition: max-width 0.2s ease-out;
        max-width: 100%;

        /* arrow rotation on open */
        summary::after {
            transform: rotate(90deg);
        }
    }

    details[open]::details-content {
        max-height: 800px;
        padding-inline: 1rem;
        padding-bottom: 0.4rem;
        /* open: height expands after width finishes */
        transition:
            max-height 0.2s ease-in 0.2s,
            padding-bottom 0.2s ease-in 0.2s,
            content-visibility 0.2s ease-in 0.2s allow-discrete;
    }

    @media (prefers-reduced-motion: reduce) {
        details,
        details[open] {
            transition: none;
        }
        details::details-content,
        details[open]::details-content {
            transition: none;
        }
        details summary::after {
            transition: none;
        }
    }
`;

export const blockquotesStyles = css`
    ${blockquoteStyle('Evidence')}
    ${blockquoteStyle('Note')}
    ${blockquoteStyle('Alarm')}
    ${blockquoteStyle('Warning')}

    ${detailsStyles}
`;
